import {
  asDateRangePreset,
  asTabFilterSpecial,
  DEFAULT_TAB_FILTER_SELECTION,
  type TabFilterSelectionConfig,
  type TabFilterDimension,
  type TabFilterOptionsMapping,
  type TabFilterOptionsSource,
  type TabFilterKind,
  type TabFilterOntologyLevel,
  type TabFilterOntologyValue,
  type TabFilterScope,
  type TabFilterValue,
} from "@/features/app-studio/model/appTabFilters";
import type { AppQueryPreview } from "@/features/app-studio/api/appQueriesApi";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * A persisted, per-tab filter *field* returned by the API. `options` are the resolved dropdown
 * choices for an `ontology` field (empty for `text`/`dateRange`), computed server-side so consumers
 * need no semantic model. `selected_value` is the field's effective value: the builder default in
 * the builder, or the viewer's preference (falling back to the default) in the published viewer;
 * `null` when nothing is configured.
 */
export type TabFilterField = {
  id: number;
  tab_id: number | null;
  by: "key" | "name";
  value: string;
  name: string;
  kind: TabFilterKind;
  sort_order: number;
  options: TabFilterOntologyValue[];
  selected_value?: TabFilterValue | null;
  /**
   * Where `options` come from: the linked ontology, or the author's `options_sql`, which the
   * server re-runs on every list so the values stay current. `options_error` explains a run
   * that produced none.
   */
  options_source: TabFilterOptionsSource;
  options_sql: string | null;
  /** Which result column is shown and which applied (date range: shown, start, end). */
  options_mapping: TabFilterOptionsMapping | null;
  options_error: string | null;
  /** The dimension's own name — what resetting the label restores. `name` is what shows. */
  dimension_name: string;
  /** The author's label for the chip; `null` when it shows the dimension's name. */
  display_name: string | null;
  /** How the selection behaves: special choices, single/multi, empty, parent changes. */
  selection: TabFilterSelectionConfig;
  /** The other filters this one's values query reads (`{{filter: column}}`), as written. */
  parents: string[];
  /**
   * Which level of which multi-column ontology the options come from, or `null`. Filters sharing an
   * `ontology_key` narrow each other, so a change to one re-evaluates the rest. Absent from
   * responses cached before multi-column ontologies existed.
   */
  ontology_level?: TabFilterOntologyLevel | null;
  /**
   * Shown on every tab of the app (tabs added later too). Listed for a tab, `tab_id` is that tab;
   * one row backs every tab, so its default and a viewer's pick carry from tab to tab.
   */
  applies_to_all_tabs: boolean;
  /**
   * Shown on exactly these tabs, one row backing them all like an every-tab filter; `null` when
   * the filter is one tab's own or on every tab.
   */
  shared_tab_ids: number[] | null;
};

/** Whether one saved row backs several tabs, so a pick on one tab carries to the others. */
export function isSharedField(field: TabFilterField): boolean {
  return field.applies_to_all_tabs || field.shared_tab_ids !== null;
}

export type AddTabFilterPayload = {
  tab_id: number | null;
  by: "key" | "name";
  value: string;
  name: string;
  kind: TabFilterKind;
};

/** A saved filter field as a dimension a filter bar can render (options come from the API). */
export function fieldToDimension(field: TabFilterField): TabFilterDimension {
  const fromQuery = field.options_source === "query";
  return {
    value: field.value,
    name: field.name,
    by: field.by,
    filterKind: field.kind,
    // A query supplies a pick list whatever the kind, so a text filter backed by one is picked
    // from its results rather than typed.
    ontologyValues: field.kind === "ontology" || fromQuery ? field.options : null,
    optionsSource: field.options_source,
    optionsSql: field.options_sql,
    optionsMapping: field.options_mapping,
    optionsError: field.options_error,
    defaultName: field.dimension_name,
    hasCustomName: field.display_name !== null,
    selectionConfig: field.selection,
    parents: field.parents,
    ...(field.ontology_level ? { ontologyLevel: field.ontology_level } : {}),
    defaultValue: field.selected_value ?? null,
    appliesToAllTabs: field.applies_to_all_tabs,
    sharedTabIds: field.shared_tab_ids,
  };
}

function filtersPath(appId: number, tabId: number | null): string {
  const base = `/v1/apps/${appId}/filters`;
  return tabId === null ? base : `${base}?tab_id=${tabId}`;
}

function asOntologyLevel(value: unknown): TabFilterOntologyLevel | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (typeof row.ontology_key !== "string" || typeof row.level !== "number") {
    return null;
  }
  return { ontologyKey: row.ontology_key, level: row.level };
}

function optionalText(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function asOptionsMapping(value: unknown): TabFilterOptionsMapping | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const row = value as Record<string, unknown>;
  return {
    label_column: optionalText(row.label_column),
    value_column: optionalText(row.value_column),
    start_column: optionalText(row.start_column),
    end_column: optionalText(row.end_column),
  };
}

function asSelectionConfig(value: unknown): TabFilterSelectionConfig {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_TAB_FILTER_SELECTION;
  }
  const row = value as Record<string, unknown>;
  const rule = row.on_parent_change;
  return {
    all_enabled: row.all_enabled === true,
    all_label: optionalText(row.all_label),
    none_enabled: row.none_enabled === true,
    none_label: optionalText(row.none_label),
    multi: typeof row.multi === "boolean" ? row.multi : null,
    empty_behavior: row.empty_behavior === "match_nothing" ? "match_nothing" : "skip",
    on_parent_change:
      rule === "first" || rule === "all" || rule === "clear" ? rule : "keep_or_first",
  };
}

function asTabFilterValue(value: unknown): TabFilterValue | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const row = value as Record<string, unknown>;
  const rawValues = Array.isArray(row.values) ? row.values : [];
  const special = asTabFilterSpecial(row.special);
  return {
    values: rawValues.filter((entry): entry is string => typeof entry === "string"),
    start_date: typeof row.start_date === "string" ? row.start_date : null,
    end_date: typeof row.end_date === "string" ? row.end_date : null,
    date_preset: asDateRangePreset(row.date_preset),
    // Only present when set, so a plain value keeps the shape it has always had.
    ...(special !== null ? { special } : {}),
  };
}

export function asTabFilterField(value: unknown): TabFilterField {
  if (typeof value !== "object" || value === null) {
    throw new Error("Unexpected response shape");
  }
  const row = value as Record<string, unknown>;
  const rawOptions = Array.isArray(row.options) ? row.options : [];
  const options = rawOptions.map((option) => {
    const record = (option ?? {}) as Record<string, unknown>;
    const parsed: TabFilterOntologyValue = {
      label: typeof record.label === "string" ? record.label : "",
      value: typeof record.value === "string" ? record.value : "",
    };
    // Only a query-backed date range's choices carry a window; others stay two-key objects.
    const startDate = optionalText(record.start_date);
    const endDate = optionalText(record.end_date);
    if (startDate !== null && endDate !== null) {
      parsed.start_date = startDate;
      parsed.end_date = endDate;
    }
    return parsed;
  });
  const ontologyLevel = asOntologyLevel(row.ontology_level);
  return {
    id: Number(row.id),
    tab_id: row.tab_id === null || row.tab_id === undefined ? null : Number(row.tab_id),
    by: row.by === "key" ? "key" : "name",
    value: String(row.value ?? ""),
    name: String(row.name ?? ""),
    kind: row.kind as TabFilterKind,
    sort_order: Number(row.sort_order ?? 0),
    options,
    selected_value: asTabFilterValue(row.selected_value),
    options_source: row.options_source === "query" ? "query" : "ontology",
    options_sql: typeof row.options_sql === "string" ? row.options_sql : null,
    options_mapping: asOptionsMapping(row.options_mapping),
    // Absent from responses predating renames: the shown name is then the dimension's own.
    dimension_name:
      typeof row.dimension_name === "string" && row.dimension_name !== ""
        ? row.dimension_name
        : String(row.name ?? ""),
    display_name: optionalText(row.display_name),
    selection: asSelectionConfig(row.selection),
    parents: Array.isArray(row.parents)
      ? row.parents.filter((parent): parent is string => typeof parent === "string")
      : [],
    options_error: typeof row.options_error === "string" ? row.options_error : null,
    // Only when the field is on a multi-column ontology's level; most fields are not.
    ...(ontologyLevel !== null ? { ontology_level: ontologyLevel } : {}),
    applies_to_all_tabs: row.applies_to_all_tabs === true,
    shared_tab_ids: Array.isArray(row.shared_tab_ids)
      ? row.shared_tab_ids.filter((tabId): tabId is number => typeof tabId === "number")
      : null,
  };
}

/** The saved filter fields for one tab (`tabId` null = the default tab / app with no tabs). */
export async function listTabFilters(appId: number, tabId: number | null): Promise<TabFilterField[]> {
  const res = await apiFetch(filtersPath(appId, tabId), { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error("Unexpected response shape");
  }
  return data.map(asTabFilterField);
}

/** Persist a new filter field on a tab. */
export async function addTabFilter(
  appId: number,
  payload: AddTabFilterPayload,
): Promise<TabFilterField> {
  const res = await apiFetch(`/v1/apps/${appId}/filters`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/** Set the builder-configured default value for a saved filter field. */
export async function updateTabFilterValue(
  appId: number,
  filterId: number,
  value: TabFilterValue,
): Promise<TabFilterField> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/${filterId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(value),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/**
 * Take a filter's choices from `sql`, read through `mapping` (which column is shown, which is
 * applied — or for a date range, which hold each choice's start and end), or pass `null` to go
 * back to the default picker. The server runs the query before saving and refuses one that fails
 * or that the mapping does not fit, so a successful response already carries the new options.
 */
export async function setTabFilterOptionsQuery(
  appId: number,
  filterId: number,
  sql: string | null,
  mapping: TabFilterOptionsMapping | null = null,
): Promise<TabFilterField> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/${filterId}/options-query`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sql, mapping }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/**
 * Offer or withdraw "All" / "No filter" on a filter with a list of values. Withdrawing the choice
 * the default is set to moves the default to "first value", which the returned field reflects.
 */
export async function setTabFilterSelectionConfig(
  appId: number,
  filterId: number,
  choices: TabFilterSelectionConfig,
): Promise<TabFilterField> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/${filterId}/selection`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(choices),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/** Rename a filter's chip, or pass `null` to show the dimension's own name again. */
export async function setTabFilterDisplayName(
  appId: number,
  filterId: number,
  displayName: string | null,
): Promise<TabFilterField> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/${filterId}/display-name`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ display_name: displayName }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/**
 * Share a filter with every tab or with selected tabs, or make it `tabId`'s own again. `tabId` is
 * the tab the author is on, and a selection must include it. Nested filters move with it — its
 * parents widen to wherever it is shown, the shared filters reading it narrow to wherever it
 * still is — and the server refuses a move that would leave another tab's filter without its
 * parent. The returned field is listed as on `tabId`.
 */
export async function setTabFilterScope(
  appId: number,
  filterId: number,
  payload: { scope: TabFilterScope; tabId: number | null },
): Promise<TabFilterField> {
  const { scope, tabId } = payload;
  const res = await apiFetch(`/v1/apps/${appId}/filters/${filterId}/scope`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      applies_to_all_tabs: scope.kind === "allTabs",
      shared_tab_ids: scope.kind === "selectedTabs" ? scope.tabIds : null,
      tab_id: tabId,
    }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/**
 * Save the order a tab lists its filters in. `filterIds` names every filter the tab shows; the
 * server keeps app-level filters ahead of the tab's own (one order on every tab), so a list mixing
 * the two is read group by group. Returns the tab's fields in their new order.
 */
export async function reorderTabFilters(
  appId: number,
  payload: { tabId: number | null; filterIds: number[] },
): Promise<TabFilterField[]> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/order`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tab_id: payload.tabId, filter_ids: payload.filterIds }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error("Unexpected response shape");
  }
  return data.map(asTabFilterField);
}

/** Remove a saved filter field. */
export async function deleteTabFilter(appId: number, filterId: number): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/${filterId}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

/** Each filter's current pick, keyed by dimension value — what the evaluate/preview calls take. */
export type TabFilterSelections = Record<string, TabFilterValue | null>;

/**
 * Re-evaluate a tab's filters for picks not saved yet: what the filter bar calls when a filter
 * that others read changes. `changed` names the filters just changed; their dependants' lists are
 * re-run with the new values and their selections follow each one's "when a parent changes" rule.
 * Nothing is stored. `published` targets the viewer's route instead of the builder's.
 */
export async function evaluateTabFilters(
  appId: number,
  payload: { tabId: number | null; selections: TabFilterSelections; changed: string[] },
  { published = false }: { published?: boolean } = {},
): Promise<TabFilterField[]> {
  const base = published ? "/v1/published-apps" : "/v1/apps";
  const res = await apiFetch(`${base}/${appId}/filters/evaluate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tab_id: payload.tabId,
      selections: payload.selections,
      changed: payload.changed,
    }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error("Unexpected response shape");
  }
  return data.map(asTabFilterField);
}

/** A values-query preview: its first rows, and the SQL as run with placeholders filled in. */
export type FilterOptionsPreview = AppQueryPreview & { resolved_sql: string | null };

/**
 * Run a values query being written, its `{{filter: column}}` placeholders filled from the
 * other filters on the tab (`selections` as on screen, else their defaults).
 */
export async function previewFilterOptionsQuery(
  appId: number,
  payload: {
    tabId: number | null;
    filterValue: string;
    sql: string;
    selections: TabFilterSelections;
  },
): Promise<FilterOptionsPreview> {
  const res = await apiFetch(`/v1/apps/${appId}/filters/options-preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tab_id: payload.tabId,
      filter_value: payload.filterValue,
      sql: payload.sql,
      selections: payload.selections,
    }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data = (await res.json()) as Record<string, unknown>;
  return {
    columns: Array.isArray(data.columns) ? (data.columns as string[]) : [],
    rows: Array.isArray(data.rows) ? (data.rows as Record<string, unknown>[]) : [],
    row_count: typeof data.row_count === "number" ? data.row_count : 0,
    truncated: data.truncated === true,
    error: typeof data.error === "string" ? data.error : null,
    resolved_sql: typeof data.resolved_sql === "string" ? data.resolved_sql : null,
  };
}
